import {CropService} from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import {TranslateService} from '@ngx-translate/core';
import {inject} from '@angular/core';
import {CropPhaseLike, daysSincePlanting, vegetationPeriodEndDay} from './crop-vegetation-phase.util';
import {lastNormSegmentValues} from './crop-chart-norm-planting.util';

// Enum for vegetation index types matching the API
export enum VegetationIndexType {
  ARVI = 1,
  EVI = 2,
  GNDVI = 3,
  MNDWI = 4,
  NDMI = 5,
  NDVI = 6,
  NDWI = 7,
  ORVI = 8,
  OSAVI = 9
}

export interface NormDataPoint {
  dayStart: number;
  dayEnd: number;
  indexValue: number;
  indexMin: number;
  indexMax: number;
}

export interface NormDataset {
  label: string;
  data: (number | null)[];
  borderColor: string;
  backgroundColor: string;
  borderWidth: number;
  borderDash: number[];
  showArea: boolean;
  dataMin?: (number | null)[];
  dataMax?: (number | null)[];
  areaOpacity: number;
  ownLabels?: string[];
  useOwnLabels?: boolean;
}

// Color map for norm curves (lighter versions)
const NORM_COLOR_MAP: Record<string, string> = {
  Arvi: '#D4E157',
  Evi: '#81C784',
  Gndvi: '#4CAF50',
  Mndwi: '#64B5F6',
  Ndmi: '#7986CB',
  Ndvi: '#66BB6A',
  Ndwi: '#4DD0E1',
  Orvi: '#80CBC4',
  Osavi: '#A5D6A7',
};

// Base configs for mock data generation
const MOCK_BASE_CONFIGS: Record<number, { baseValue: number; amplitude: number; peakDay: number }> = {
  [VegetationIndexType.NDVI]: { baseValue: 0.3, amplitude: 0.5, peakDay: 90 },
  [VegetationIndexType.EVI]: { baseValue: 0.25, amplitude: 0.45, peakDay: 85 },
  [VegetationIndexType.GNDVI]: { baseValue: 0.35, amplitude: 0.4, peakDay: 88 },
  [VegetationIndexType.NDMI]: { baseValue: 0.1, amplitude: 0.3, peakDay: 80 },
  [VegetationIndexType.NDWI]: { baseValue: -0.1, amplitude: 0.25, peakDay: 75 },
  [VegetationIndexType.ARVI]: { baseValue: 0.28, amplitude: 0.42, peakDay: 92 },
  [VegetationIndexType.MNDWI]: { baseValue: -0.2, amplitude: 0.35, peakDay: 70 },
  [VegetationIndexType.ORVI]: { baseValue: 0.32, amplitude: 0.38, peakDay: 87 },
  [VegetationIndexType.OSAVI]: { baseValue: 0.3, amplitude: 0.48, peakDay: 89 },
};

export class NormDataManager {
  // Toggle this to use mock norm data for testing
  public useMockData = false;
  private translateService:TranslateService = inject(TranslateService);


  constructor(
    private cropService: CropService) {}

  getNormColor(indexValue: string): string {
    return NORM_COLOR_MAP[indexValue] || '#999999';
  }

  async loadNormData(
    vegetationIndexType: VegetationIndexType,
    cropId: string
  ): Promise<NormDataPoint[]> {
    if (this.useMockData) {
      return this.generateMockNormData(vegetationIndexType);
    }

    try {
      const response = await this.cropService.dictionariesCropGetCropIndicesPost({
        vegetationIndexType: vegetationIndexType,
        cropId: cropId
      }).toPromise() as any;
      if (Array.isArray(response)) {
        return response.map(r => ({
          dayStart: r.dayStart,
          dayEnd: r.dayEnd,
          indexValue: r.indexValue ?? r.value ?? null,
          indexMin: r.indexMin ?? null,
          indexMax: r.indexMax ?? null
        }));
      }
      return [];
    } catch {
      return [];
    }
  }

  createNormDataset(
    normData: NormDataPoint[],
    chartLabels: string[],
    plantingDate: string | undefined,
    indexLabel: string,
    indexValue: string,
    vegetationPhases: CropPhaseLike[] = [],
  ): NormDataset | null {
    if (!normData.length) {
      return null;
    }

    const {labels, data, dataMin, dataMax} = this.generateNormCurveData(
      normData,
      plantingDate,
      chartLabels,
      vegetationPhases,
    );

    if (!data.length) {
      console.warn('No norm data generated');
      return null;
    }

    const hasMinMax = dataMin.some(v => v !== null) && dataMax.some(v => v !== null);
    const normColor = this.getNormColor(indexValue);

    console.log('Norm curve generated:', {
      plantingDate,
      labels,
      data,
      dataMin,
      dataMax
    });

    return {
      label: `${indexLabel} (${this.translateService.instant('Norm')})`,
      borderColor: normColor,
      backgroundColor: normColor,
      borderWidth: 2,
      borderDash: [4, 4],
      showArea: hasMinMax,
      areaOpacity: 0.2,
      ownLabels: labels,
      useOwnLabels: true,
      data,
      dataMin: hasMinMax ? dataMin : undefined,
      dataMax: hasMinMax ? dataMax : undefined
    };
  }

  /**
   * Génère les données de la courbe de norme avec ses propres labels
   * basés sur dayStart et dayEnd
   */
  private generateNormCurveData(
    normData: NormDataPoint[],
    plantingDate: string | undefined,
    chartLabels: string[],
    vegetationPhases: CropPhaseLike[] = [],
  ): {labels: string[]; data: (number | null)[]; dataMin: (number | null)[]; dataMax: (number | null)[]} {
    const sortedNormData = [...normData].sort((a, b) => a.dayStart - b.dayStart);
    if (!sortedNormData.length) {
      return {labels: [], data: [], dataMin: [], dataMax: []};
    }

    const minNormDay = Math.min(...sortedNormData.map(n => n.dayStart));
    const maxNormDay = Math.max(...sortedNormData.map(n => n.dayEnd));
    const vegEndDay = vegetationPeriodEndDay(vegetationPhases);
    const lastNorm = lastNormSegmentValues(sortedNormData);

    // Anchor rule:
    // - if index data labels exist => map dayStart/dayEnd as day offsets from min index date
    // - if no index labels => render norms over their own [minDayStart..maxDayEnd] range (visible fallback)
    const validChartDates = (chartLabels ?? [])
      .map(d => new Date(d))
      .filter(d => !Number.isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());

    let axisStartDate: Date;
    let axisMinDay: number;
    let axisMaxDay: number;

    if (validChartDates.length > 0) {
      axisStartDate = new Date(validChartDates[0]);
      axisStartDate.setHours(0, 0, 0, 0);
      axisMinDay = 0;
      axisMaxDay = maxNormDay;
      if (vegEndDay != null && plantingDate) {
        const plant = new Date(plantingDate);
        if (!Number.isNaN(plant.getTime())) {
          plant.setHours(0, 0, 0, 0);
          const anchorOffset = daysSincePlanting(plant, validChartDates[0]);
          axisMaxDay = Math.min(axisMaxDay, Math.max(0, vegEndDay - anchorOffset));
        }
      }
    } else {
      const base = plantingDate ? new Date(plantingDate) : new Date();
      base.setHours(0, 0, 0, 0);
      axisStartDate = base;
      axisMinDay = minNormDay;
      axisMaxDay = maxNormDay;
      if (vegEndDay != null) {
        axisMaxDay = Math.min(axisMaxDay, vegEndDay);
      }
    }

    const dayToIso = (dayOffset: number): string => {
      const d = new Date(axisStartDate);
      d.setDate(d.getDate() + (dayOffset - axisMinDay));
      return d.toISOString().split('T')[0];
    };

    const labels: string[] = [];
    const data: (number | null)[] = [];
    const dataMin: (number | null)[] = [];
    const dataMax: (number | null)[] = [];

    // Ensure domain endpoints exist so the curve is always visible on [min..max] when index data is absent.
    const domainStartLabel = dayToIso(axisMinDay);
    if (!labels.includes(domainStartLabel)) {
      labels.push(domainStartLabel);
      data.push(sortedNormData[0].indexValue);
      dataMin.push(sortedNormData[0].indexMin);
      dataMax.push(sortedNormData[0].indexMax);
    }

    // Générer un point pour chaque transition (dayStart et dayEnd de chaque plage)
    sortedNormData.forEach((norm, index) => {
      const normStart =
        vegEndDay != null && norm.dayStart > vegEndDay ? vegEndDay : norm.dayStart;
      const normEnd =
        vegEndDay != null && norm.dayEnd > vegEndDay ? vegEndDay : norm.dayEnd;
      const value =
        vegEndDay != null && norm.dayStart > vegEndDay && lastNorm ? lastNorm.indexValue : norm.indexValue;
      const minVal =
        vegEndDay != null && norm.dayStart > vegEndDay && lastNorm ? lastNorm.indexMin : norm.indexMin;
      const maxVal =
        vegEndDay != null && norm.dayStart > vegEndDay && lastNorm ? lastNorm.indexMax : norm.indexMax;

      const startLabel = dayToIso(normStart);

      // Ajouter le point de début seulement s'il n'est pas déjà ajouté
      if (!labels.includes(startLabel)) {
        labels.push(startLabel);
        data.push(value);
        dataMin.push(minVal);
        dataMax.push(maxVal);
      }

      const endLabel = dayToIso(normEnd);

      if (endLabel !== startLabel && !labels.includes(endLabel)) {
        labels.push(endLabel);
        data.push(value);
        dataMin.push(minVal);
        dataMax.push(maxVal);
      }

      // Gérer le gap entre cette plage et la suivante (interpolation)
      if (index < sortedNormData.length - 1) {
        const nextNorm = sortedNormData[index + 1];
        const gapStart = norm.dayEnd;
        const gapEnd = nextNorm.dayStart;

        // S'il y a un gap, ajouter un point interpolé au milieu
        if (gapEnd - gapStart > 1) {
          const midDay = Math.floor((gapStart + gapEnd) / 2);
          const midLabel = dayToIso(midDay);

          if (!labels.includes(midLabel)) {
            // Interpolation linéaire
            const t = (midDay - gapStart) / (gapEnd - gapStart);
            labels.push(midLabel);
            data.push(norm.indexValue + (nextNorm.indexValue - norm.indexValue) * t);
            dataMin.push(norm.indexMin + (nextNorm.indexMin - norm.indexMin) * t);
            dataMax.push(norm.indexMax + (nextNorm.indexMax - norm.indexMax) * t);
          }
        }
      }
    });

    const domainEndLabel = dayToIso(axisMaxDay);
    if (!labels.includes(domainEndLabel)) {
      const last = sortedNormData[sortedNormData.length - 1];
      labels.push(domainEndLabel);
      data.push(last.indexValue);
      dataMin.push(last.indexMin);
      dataMax.push(last.indexMax);
    }

    return { labels, data, dataMin, dataMax };
  }

  /**
   * Generate mock norm data for testing the area band visualization
   */
  private generateMockNormData(vegetationIndexType: VegetationIndexType): NormDataPoint[] {
    const config = MOCK_BASE_CONFIGS[vegetationIndexType] || { baseValue: 0.3, amplitude: 0.4, peakDay: 90 };
    const mockData: NormDataPoint[] = [];

    // Generate data for 180 days in 10-day intervals
    for (let day = 0; day <= 180; day += 10) {
      const normalizedDay = (day - config.peakDay) / 60;
      const growthFactor = Math.exp(-normalizedDay * normalizedDay);

      const indexValue = config.baseValue + (config.amplitude * growthFactor);
      const variance = 0.08 + (0.05 * growthFactor);
      const indexMin = indexValue - variance;
      const indexMax = indexValue + variance;

      mockData.push({
        dayStart: day,
        dayEnd: day + 9,
        indexValue: Math.round(indexValue * 1000) / 1000,
        indexMin: Math.round(indexMin * 1000) / 1000,
        indexMax: Math.round(indexMax * 1000) / 1000
      });
    }

    return mockData;
  }
}
